import { computed, type ComputedRef, type Ref } from 'vue';

import { amplitudeLib } from '../../shared/lib';
import { CROSS_APP_CACHED_EXPERIMENTS } from './config';

const EXPERIMENT_CACHE_PREFIX = 'free:exp:';

const getCachedVariant = (experimentKey: string): string | null => {
  try {
    return localStorage.getItem(`${EXPERIMENT_CACHE_PREFIX}${experimentKey}`);
  } catch {
    return null;
  }
};

const setCachedVariant = (experimentKey: string, variant: string) => {
  try {
    localStorage.setItem(`${EXPERIMENT_CACHE_PREFIX}${experimentKey}`, variant);
  } catch {
    // localStorage may be unavailable
  }
};

export const checkExperimentVariant = (
  experimentKey: string,
  variantToCheck: string,
) => {
  const { experiment, isExperimentLoading } =
    amplitudeLib.useAmplitudeExperiment();

  const isCrossAppCached = CROSS_APP_CACHED_EXPERIMENTS.has(experimentKey);
  const cachedVariant = isCrossAppCached
    ? getCachedVariant(experimentKey)
    : null;

  const currentVariant = computed(() => {
    /**
     * Read unconditionally, even where the value is not used: it is the only
     * signal that the SDK's variants have arrived.
     *
     * The client lives in a `shallowRef` and is assigned BEFORE `start()`
     * resolves, and it fills its variants inside itself — so when they finally
     * arrive nothing reactive is written and this computed would keep the
     * answer it snapshotted from a client that had no variants yet. The gate
     * then said "not the variant" for the whole page load, and only the SDK's
     * own localStorage cache made it answer on the next one: the avia AI chat
     * appeared only after a second reload (retest QA 01.09, item 37). Touching
     * the flag here makes the flip to `false` invalidate the answer.
     */
    const isLoading = isExperimentLoading.value;

    const serverVariant = experiment.value?.variant?.(experimentKey)?.key;
    if (isCrossAppCached) {
      // While experiment is still loading, the SDK may return a stale variant
      // from its own localStorage cache (e.g. written by railways with a
      // different user ID). Use our own cache to prevent UI flicker.
      if (isLoading) {
        return cachedVariant ?? serverVariant ?? null;
      }

      // After loading completes we have a fresh server response — update cache.
      if (serverVariant) {
        setCachedVariant(experimentKey, serverVariant);
      }

      return serverVariant ?? cachedVariant;
    }

    return serverVariant ?? null;
  });

  return {
    isExperimentLoading,
    isVariant: computed(() => currentVariant.value === variantToCheck),
  };
};

/**
 * Reads the current variant key for an experiment from the SDK without going
 * through a Vue computed/ref. Use from non-reactive contexts such as analytics
 * event helpers that fire on a later page (after MainPage is unmounted) and
 * still need to attach the originally-resolved variant to event properties.
 *
 * Returns `null` when the SDK has not yet returned a variant (e.g. before the
 * user has visited a page that calls `checkExperimentVariant`).
 */
export const getCurrentVariant = (experimentKey: string): string | null => {
  const { experiment } = amplitudeLib.useAmplitudeExperiment();
  return experiment.value?.variant?.(experimentKey)?.key ?? null;
};

/**
 * Reactively exposes the JSON payload attached to an experiment's resolved
 * variant (Amplitude lets a variant carry an arbitrary payload alongside its
 * key). Use when the experiment ships data — e.g. a list config — rather than
 * just gating a boolean. `payload` is `null` until the SDK resolves the variant
 * or when the variant has no payload; callers should gate on
 * `isExperimentLoading` before treating `null` as "empty".
 */
export const getExperimentPayload = <TPayload = unknown>(
  experimentKey: string,
): {
  payload: ComputedRef<TPayload | null>;
  isExperimentLoading: Ref<boolean>;
} => {
  const { experiment, isExperimentLoading } =
    amplitudeLib.useAmplitudeExperiment();

  const payload = computed<TPayload | null>(() => {
    // Same reason as in `checkExperimentVariant`: the flag is the only reactive
    // signal that the SDK's variants have arrived, so without reading it here
    // the payload would stay at the `null` snapshotted before `start()`.
    void isExperimentLoading.value;
    return (experiment.value?.variant?.(experimentKey)?.payload ??
      null) as TPayload | null;
  });

  return { payload, isExperimentLoading };
};

/**
 * Returns a cached-only experiment variant.
 * Does NOT use server response and does NOT write to cache.
 * Use in apps where Amplitude user ID differs from other apps (e.g. railways),
 * so the server variant would be incorrect for shared experiments.
 *
 * @param defaultVariant - fallback when cache is empty
 */
export const checkCachedExperimentVariant = (
  experimentKey: string,
  variantToCheck: string,
  defaultVariant: string,
) => {
  const { isExperimentLoading } = amplitudeLib.useAmplitudeExperiment();

  const cachedVariant = getCachedVariant(experimentKey);
  const currentVariant = cachedVariant ?? defaultVariant;

  return {
    isExperimentLoading,
    isVariant: computed(() => currentVariant === variantToCheck),
  };
};
